import React, { useCallback, useRef, useState } from "react";

const SRCSET_ORDER = ["thumb", "card", "mobile", "desktop"];

const buildSrcSet = (variants) => {
  if (!variants) return "";
  const entries = SRCSET_ORDER
    .map((name) => variants[name])
    .filter((v) => v?.url && v?.width)
    .map((v) => `${v.url} ${v.width}w`);
  return entries.length > 1 ? entries.join(", ") : "";
};

/**
 * Scales any uploaded image to its container automatically:
 * portrait/panoramic uploads switch to `contain` (whole image visible, no crop)
 * over a blurred fill of themselves, while images close to the container ratio
 * stay `cover`. When upload variants are available the browser downloads the
 * smallest file that still looks sharp.
 */
export const SmartImage = ({
  src,
  alt = "",
  variants,
  sizes = "(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw",
  className = "",
  imgClassName = "",
  tolerance = 0.3,
  fit = "auto",
  loading = "lazy",
  baseVariant = "thumb",
  children,
}) => {
  const wrapRef = useRef(null);
  const [contain, setContain] = useState(false);
  const srcSet = buildSrcSet(variants);
  // Keep the plain src light too: the preload scanner fetches it before layout.
  const baseSrc = (srcSet && variants?.[baseVariant]?.url) || src;

  const onLoad = useCallback((e) => {
    if (fit === "cover") return;
    const img = e.currentTarget;
    const box = wrapRef.current?.getBoundingClientRect();
    if (!box || !box.width || !box.height || !img.naturalWidth) return;
    const imageRatio = img.naturalWidth / img.naturalHeight;
    const boxRatio = box.width / box.height;
    setContain(Math.abs(imageRatio - boxRatio) / boxRatio > tolerance);
  }, [tolerance, fit]);

  return (
    <span ref={wrapRef} className={`relative block overflow-hidden ${className}`}>
      {contain && (
        <img
          src={baseSrc}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-45"
        />
      )}
      <img
        src={baseSrc}
        srcSet={srcSet || undefined}
        sizes={srcSet ? sizes : undefined}
        alt={alt}
        loading={loading}
        decoding="async"
        onLoad={onLoad}
        className={`relative w-full h-full ${contain ? "object-contain" : "object-cover"} ${imgClassName}`}
      />
      {children}
    </span>
  );
};
